// Ringkasan. Concept lines (JEV ui.region_gate, all kept):
//   ov_header  Job: orient, which screen and how fresh. Message: Ringkasan,
//              updated at a clock time. Action: none. Container: plain spacing.
//   ov_stats   Job: the bot's state at a glance. Message: paired senders,
//              submissions today and in total, active codes. Action: none.
//              Container: bento, kit BentoTile stat tiles on a declared map
//              (JEV ui.component_recipe stats_layout six_col_lead_half):
//              Kiriman hari ini is the lead tile, half the row from 1024
//              ("b b b a c d"), a full row above the other three below 1024.
//   ov_latest  Job: see the newest arrivals and jump to the list. Message:
//              the 5 latest submissions, masked. Action: Lihat semua.
//              Container: rows, recipe core.table on the same fixed column
//              grid as Kiriman (SubmissionRecords), stacked below 1024.
// The screen is one kit Section (custom: an Operate screen opens on a page
// header, not a Masthead, and the kit has no record list composition); the
// stat tiles are the kit's own BentoTile on its .kit-bento grid, validated
// with validateBentoLayout. Polls every 10 s; the meta line shows the time.
import { useId, type CSSProperties } from "react";
import { BentoTile, Section, validateBentoLayout } from "@bot-whatsapp/ui";
import { useDashboardApi } from "./api";
import { useDelayed, useResource } from "./data";
import { formatClock, formatDateTime, formatNumber, formatTime } from "./format";
import { EmptyState, LoadError, RowsSkeleton, ScreenHeader, SubmissionRecords } from "./ui";

// Six tracks from 1024 so the lead spans half the row; three tracks below
// (the lead on its own row). The kit validator takes the track budget as its
// third argument; every map is still checked for dead cells and rectangles.
const STAT_LAYOUT = { lg: ["b b b a c d"], md: ["b b b", "a c d"] };
const STAT_TILES = [
  { area: "a", kind: "stat" as const },
  { area: "b", kind: "stat" as const },
  { area: "c", kind: "stat" as const },
  { area: "d", kind: "stat" as const },
];
const layoutErrors = [
  ...validateBentoLayout(STAT_LAYOUT.lg, STAT_TILES, 6),
  ...validateBentoLayout(STAT_LAYOUT.md, STAT_TILES, 3),
];
if (layoutErrors.length) throw new Error(`Ringkasan stat map: ${layoutErrors.join("; ")}`);

const bentoStyle = {
  "--kit-bento-lg": '"b b b a c d"',
  "--kit-bento-lg-cols": "6",
  "--kit-bento-md": '"b b b" "a c d"',
  "--kit-bento-md-cols": "3",
} as CSSProperties;

export const POLL_MS = 10_000;

function isToday(iso: string): boolean {
  return new Date(iso).toDateString() === new Date().toDateString();
}

export function OverviewScreen() {
  const api = useDashboardApi();
  const headingId = useId();
  const latestId = useId();
  const overview = useResource(() => api.overview(), [api], { pollMs: POLL_MS });
  const latest = useResource(() => api.listSubmissions({ page: 1, pageSize: 5 }), [api], { pollMs: POLL_MS });
  const slow = useDelayed(overview.loading && !overview.data);

  // One line of day context in the lead tile: when the newest submission
  // arrived and from which sender (the first row of the latest list).
  const newest = latest.data?.items[0];
  const leadContext = !latest.data
    ? "Memuat kiriman terakhir."
    : !newest
      ? "Belum ada kiriman."
      : `Terakhir ${isToday(newest.receivedAt) ? `pukul ${formatTime(newest.receivedAt)}` : formatDateTime(newest.receivedAt)} dari ${newest.senderLabel ?? "pengirim tanpa label"}.`;
  const o = overview.data;

  const meta = overview.refreshedAt
    ? `Diperbarui pukul ${formatClock(overview.refreshedAt)}, otomatis setiap 10 detik.`
    : overview.error
      ? "Data belum dapat dimuat."
      : "Memuat data.";

  // Values stay static on change (product KPIs never count up): a fragment
  // value, not a plain string, keeps Figure from arming the count, and adds
  // no inline box of its own inside the figure.
  // Before the first answer the value slot holds a skeleton block, never a
  // fake 0; it appears only after the 300ms wait.
  const figure = (n: number | undefined) =>
    n === undefined ? (
      <span className="dash-skeleton" data-size="figure" data-shown={slow || undefined} aria-hidden="true" />
    ) : (
      <>{formatNumber(n)}</>
    );

  return (
    <Section id="ringkasan" className="dash-screen" composition="custom" variant="overview" labelledBy={headingId}>
      <ScreenHeader id={headingId} title="Ringkasan" meta={meta} live />
      {overview.error && !o ? (
        <div className="kit-full">
          <LoadError what="Ringkasan" onRetry={overview.reload} />
        </div>
      ) : (
        <div className="kit-bento dash-stats" data-has-md="" style={bentoStyle} aria-busy={!o || undefined}>
          <BentoTile
            area="b"
            kind="stat"
            value={figure(o?.submissionsToday)}
            body={
              <>
                Kiriman hari ini
                <span className="dash-lead-context">{leadContext}</span>
              </>
            }
            signal
          />
          <BentoTile area="a" kind="stat" value={figure(o?.pairedSenders)} body="Pengirim aktif" />
          <BentoTile area="c" kind="stat" value={figure(o?.submissionsTotal)} body="Total kiriman" />
          <BentoTile area="d" kind="stat" value={figure(o?.activeCodes)} body="Kode aktif" />
        </div>
      )}
      <section className="dash-region kit-full" aria-labelledby={latestId}>
        <div className="dash-region-head">
          <h2 id={latestId} className="kit-title">
            Kiriman terbaru
          </h2>
          <a className="btn btn-ghost" href="#kiriman" aria-label="Lihat semua kiriman">
            Lihat semua
          </a>
        </div>
        {latest.error && !latest.data ? (
          <LoadError what="Kiriman terbaru" onRetry={latest.reload} />
        ) : !latest.data ? (
          <RowsSkeleton rows={5} />
        ) : latest.data.items.length === 0 ? (
          <EmptyState
            title="Belum ada kiriman"
            body="Kiriman muncul di sini setelah pengirim yang terpasang mengirim nama dan NIK ke bot."
            action={
              <a className="btn btn-secondary" href="#kode">
                Buat kode pairing
              </a>
            }
          />
        ) : (
          <SubmissionRecords items={latest.data.items} />
        )}
      </section>
    </Section>
  );
}
